import React, { useRef, useEffect, useState } from 'react'
import { Bold, Italic, Underline, List, ListOrdered, Image as ImageIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface HTMLEditorProps {
  id?: string
  value?: string
  onChange?: (html: string) => void
  placeholder?: string
  rows?: number
  className?: string
  disabled?: boolean
}

export const HTMLEditor: React.FC<HTMLEditorProps> = ({
  id,
  value = '',
  onChange,
  placeholder = '',
  rows = 4,
  className,
  disabled = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const [_isActive, setIsActive] = useState(false)
  const isInitializedRef = useRef(false)

  // Initialize editor content on mount, then sync when parent value changes
  // (e.g. async edit load). Skip sync while the user is typing.
  useEffect(() => {
    if (!editorRef.current) {
      return
    }
    if (!isInitializedRef.current) {
      editorRef.current.innerHTML = value || ''
      isInitializedRef.current = true
      return
    }
    if (document.activeElement === editorRef.current) {
      return
    }
    const current = editorRef.current.innerHTML
    const next = value || ''
    if (current !== next) {
      editorRef.current.innerHTML = next
    }
  }, [value])

  const handleInput = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML)
    }
  }

  const applyStyle = (command: string, value?: string) => {
    document.execCommand(command, false, value)
    editorRef.current?.focus()
  }

  const toggleList = (ordered: boolean) => {
    applyStyle(ordered ? 'insertOrderedList' : 'insertUnorderedList')
  }

  const handleInsertImage = () => {
    const url = window.prompt('Enter image URL (https://...)')
    if (!url) {
      return
    }
    applyStyle('insertImage', url)
    handleInput()
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/html') || e.clipboardData.getData('text/plain')
    document.execCommand('insertHTML', false, text)
  }

  return (
    <div className={cn('border rounded-md overflow-hidden bg-white', disabled && 'opacity-50 pointer-events-none')}>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 border-b bg-gray-50 p-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('bold')}
          title="Bold (Ctrl+B)"
          className="h-8 w-8 p-0"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('italic')}
          title="Italic (Ctrl+I)"
          className="h-8 w-8 p-0"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('underline')}
          title="Underline (Ctrl+U)"
          className="h-8 w-8 p-0"
        >
          <Underline className="h-4 w-4" />
        </Button>
        <div className="w-px bg-gray-300" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => toggleList(false)}
          title="Bullet List"
          className="h-8 w-8 p-0"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => toggleList(true)}
          title="Numbered List"
          className="h-8 w-8 p-0"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <div className="w-px bg-gray-300" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleInsertImage}
          title="Insert Image"
          className="h-8 w-8 p-0"
        >
          <ImageIcon className="h-4 w-4" />
        </Button>
        <div className="w-px bg-gray-300" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('removeFormat')}
          title="Clear Formatting"
          className="h-8 w-8 p-0 text-xs"
        >
          ✕
        </Button>
      </div>

      {/* Content Area */}
      <div
        ref={editorRef}
        id={id}
        contentEditable={!disabled}
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={() => setIsActive(true)}
        onBlur={() => setIsActive(false)}
        suppressContentEditableWarning
        className={cn(
          'min-h-24 p-3 prose prose-sm max-w-none focus:outline-none',
          'text-sm text-foreground',
          'overflow-y-auto',
          className,
        )}
        style={{ minHeight: `${(rows ?? 4) * 1.5}rem` }}
        data-placeholder={placeholder}
      />
    </div>
  )
}
